import {
    CreateCurationAgentSourceRequest,
    CurationAgentSourceResponse,
    CurationAgentSourceStatusEnum
} from '../../api/agents/models/curation-agent.api';
import { MonitoringSeriesTypeEnum } from '../../Monitor/Shared/enums/monitoring-series-type.enum';
import { MonitoringBackEndDataSourceModel } from '../../Monitor/Shared/models/monitoring-back-end-data-source.model';
import { MentionSearchCriteriaModel } from '../../Monitor/Shared/models/mention-search-criteria.model';
import { SearchCriteriaQueryModel } from '../../api/mentions/model/search-criteria-query.model';

/** Days of history the quota estimate is averaged over, per the spec: ([7 day volume] / 7) * 30 */
export const quotaSampleDays: number = 7;
/** Days the averaged daily volume is projected across */
export const quotaProjectionDays: number = 30;



/**
 * One row of the Sources list. Wraps the value the monitoring source picker emits so the panel can
 * hang the quota estimate off it and serialise it without knowing about search criteria internals.
 */
export class CurationAgentSourceModel {
    /** Set once the source has been persisted against its agent */
    id: number = null;
    status: CurationAgentSourceStatusEnum = CurationAgentSourceStatusEnum.active;
    consecutiveFailureCount: number = 0;
    /** Epoch ms, null until the agent has run against this source */
    lastScannedAt: number = null;

    /**
     * Mentions the agent looked at, and kept, over the last 24 hours. Neither is on the source
     * response - they are stamped on from the quota usage and mention search endpoints by whoever
     * loaded the source, since only some of the views that show a source report on them.
     */
    analyzedLast24h: number = 0;
    curatedLast24h: number = 0;

    /** What the source picker is bound to. Never null so the picker always has something to write into */
    dataSource: MonitoringBackEndDataSourceModel;

    /** Mentions the source produced over the last {@link quotaSampleDays} days, null until counted */
    sampleMentionCount: number = null;

    /** The saved mention search the row's filters live in, once they have been persisted */
    filterSearchCriteriaId: number = null;

    /** The filters as last persisted, so an edit can be told apart from a reload */
    private persistedFilter: string = null;

    constructor(apiModel?: CurationAgentSourceResponse) {
        if (!apiModel) {
            this.dataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.topic);
            return;
        }

        this.id = apiModel.id;
        this.status = apiModel.status;
        this.consecutiveFailureCount = apiModel.consecutiveFailureCount;
        this.lastScannedAt = apiModel.lastScannedAt;
        this.filterSearchCriteriaId = apiModel.filterSearchCriteriaId ?? null;

        this.dataSource = new MonitoringBackEndDataSourceModel(
            apiModel.sourceType as MonitoringSeriesTypeEnum,
            Number(apiModel.sourceRef)
        );
    }

    /** Whether the agent is currently pulling from this source. Bound to the row's on/off switch */
    get isActive(): boolean {
        return this.status === CurationAgentSourceStatusEnum.active;
    }

    get hasError(): boolean {
        return this.status === CurationAgentSourceStatusEnum.error;
    }

    /**
     * Parked by the service because the account passed its quota. It resumes on its own when the
     * pool resets, and the API rejects resuming it by hand, so the row's switch is held disabled.
     */
    get isStandby(): boolean {
        return this.status === CurationAgentSourceStatusEnum.standby;
    }

    /** A source counts as chosen once the picker has resolved it to something searchable */
    isComplete(): boolean {
        return !!this.dataSource?.type && !!this.dataSource?.id;
    }

    /**
     * Average daily volume over the sample window projected across a month. Derived on the client -
     * the API exposes an account-wide pool rather than a per source estimate. Null while the count is
     * still unknown, which the banner renders as "N/A".
     */
    get estimatedMonthlyQuota(): number {
        if (this.sampleMentionCount == null) {
            return null;
        }

        return Math.round(this.sampleMentionCount / quotaSampleDays * quotaProjectionDays);
    }

    /** Whether the picker has anything narrowing the source beyond the source itself */
    get hasFilters(): boolean {
        const filters = this.dataSource?.filterSearchCriteria?.query;

        return !!filters && !filters.isEmpty();
    }

    /** Whether the filters differ from the ones already saved, and so need writing again */
    get filterChanged(): boolean {
        return this.filterSignature !== this.persistedFilter;
    }

    /** The filters as they would be saved. Dates are left out - the picker sets its own per type */
    get filterQuery(): SearchCriteriaQueryModel {
        if (!this.hasFilters) {
            return null;
        }

        const query = this.dataSource.filterSearchCriteria.query.getApiEntity();

        return { ...query, harvestedDate: null, publicationDate: null, savedDate: null, editedDate: null };
    }

    /** Called once the filters are in a saved search, so a later edit is seen as a change */
    filterPersisted(filterSearchCriteriaId: number): void {
        this.filterSearchCriteriaId = filterSearchCriteriaId;
        this.persistedFilter = this.filterSignature;
    }

    /** Called when a saved row's filters are read back in, which is the state to compare against */
    filterLoaded(filterSearchCriteria: MentionSearchCriteriaModel): void {
        this.dataSource.filterSearchCriteria = filterSearchCriteria;
        this.persistedFilter = this.filterSignature;
    }

    /** True when this row points at the same thing, filters included, as an already saved source */
    matches(apiModel: CurationAgentSourceResponse): boolean {
        return apiModel.sourceType === this.dataSource?.type
            && apiModel.sourceRef === String(this.dataSource?.id)
            && (apiModel.filterSearchCriteriaId ?? null) === this.filterSearchCriteriaId;
    }

    toCreateRequest(): CreateCurationAgentSourceRequest {
        const request: CreateCurationAgentSourceRequest = {
            sourceType: this.dataSource.type,
            sourceRef: String(this.dataSource.id)
        };

        if (this.filterSearchCriteriaId) {
            request.filterSearchCriteriaId = this.filterSearchCriteriaId;
        }

        return request;
    }

    private get filterSignature(): string {
        const query = this.filterQuery;

        return query ? JSON.stringify(query) : null;
    }
}
